import { ChatComposerContainerProps, ChatComposerViewProps } from './chatComposerProps';
import { ChatComposerController, ChatComposerLoader } from './chatComposerInterface';
import { ReportingShellContainerBaseConfig } from '../../../../base';
import { useRef } from 'react';
import { MdOutlinePlayCircle } from 'react-icons/md';
import { HiOutlineCursorClick } from 'react-icons/hi';
import { BsPersonWorkspace, BsQuestionCircle } from "react-icons/bs";

import { ChatContextProvider } from '../../detail';

export function createChatComposerContainer(
    config: ReportingShellContainerBaseConfig & {
        chat: {
            context: ChatContextProvider;
        };
    },
    loader: ChatComposerLoader,
    controller: ChatComposerController,
    View: React.FC<ChatComposerViewProps>
): React.FC<ChatComposerContainerProps> {
    const {
        context: { useContext },
        chat: {
            context: { useContext: useChatContext },
        },
        state: states,
    } = config;

    return states.composer.subscribe(({ ...containerProps }) => {
        const context = {
            root: useContext(),
            chat: useChatContext(),
        };

        const state = states.composer.useState(context.root);
        const chatState = states.chat.useState(context.root);
        const composerRef = useRef<HTMLTextAreaElement>(null);
        const data = loader.useLoad(context.root, {
            thread: containerProps.thread
                ? {
                      id: containerProps.thread,
                  }
                : null,
        });
        const props = controller.useProps(context.root, data, {
            ref: {
                composer: composerRef,
            },
            text: {
                value: state.value.text,
                onChange(value) {
                    state.onChange({ ...state.value, text: value });
                },
            },
            assistantMode: {
                value: chatState.value.assistantMode,
                onChange(value) {
                    chatState.onChange({ ...state.value, assistantMode: value });
                },
                options: [
                    {
                        id: 'workspace',
                        name: 'Workspace',
                        description: 'Perform tasks in the workspace',
                        Icon: BsPersonWorkspace  ,
                    },
                    {
                        id: 'ask',
                        name: 'Ask',
                        description: 'Ask questions about content',
                        Icon: BsQuestionCircle,
                    },
                ],
            },
            mode: {
                value: state.value.mode,
                onChange(value) {
                    state.onChange({ ...state.value, mode: value });
                },
                options: [
                    {
                        id: 'automatic',
                        name: 'Automatic',
                        description:
                            'The composer will automatically navigate to new documents',
                        Icon: MdOutlinePlayCircle,
                    },
                    {
                        id: 'manual',
                        name: 'Manual',
                        description:
                            'Navigation is manual and must explicitly be triggered',
                        Icon: HiOutlineCursorClick,
                    },
                ],
            },
            form: {
                isSubmitting: state.value.isSubmitting,
                async onSubmit(values) {
                    console.log('submitting composer', values);
                    try {
                        state.onChange({ text: '', isSubmitting: true });
                        composerRef.current?.focus();
                        const response = await containerProps.onSubmit({
                            ...state.value,
                            text: values.text,
                        });
                        console.log('response', response);
                    } finally {
                        state.onChange({ isSubmitting: false });
                    }
                },
            },
        });
        return <View {...props} />;
    });
}
